import React, { useMemo } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { DateTime } from 'luxon';

import { AppointmentSlotFragment } from '@/graphql/types-and-hooks';
import { TimePickerNavigation } from './TimePickerNavigation';
import { TimePickerHeader } from './TimePickerHeader';
import { TimePickerContent } from './TimePickerContent';

export type TimePickerDateTime = {
  date: string;
  formattedDate: string;
  time?: string;
  formattedTime?: string;
  duration?: string;
};

export interface TimePickerProps {
  slots?: AppointmentSlotFragment[];
  onChange: (obj: TimePickerDateTime) => void;
  onPrev: () => void;
  onNext: () => void;
  showPrev?: boolean;
  showNext?: boolean;
  value: TimePickerDateTime;
}

export const TimePicker = (props: TimePickerProps) => {
  const {
    slots = [],
    onChange,
    onPrev,
    onNext,
    showPrev = true,
    showNext = true,
    value,
  } = props;

  const maxAppointmentCount = useMemo(
    () => Math.max(...slots.map(slot => slot.appointments.length)),
    [slots]
  );

  const dates = useMemo(
    () =>
      slots?.map(slot =>
        DateTime.fromISO(slot.date).toFormat('EEE d LLL').split(' ')
      ),
    [slots]
  );

  const times: TimePickerDateTime[][] = useMemo(
    () =>
      slots?.map(slot => [
        ...slot.appointments.map(appointment => ({
          date: slot.date,
          formattedDate: DateTime.fromISO(slot.date).toFormat('EEE d LLL'),
          time: appointment.time,
          formattedTime: DateTime.fromISO(appointment.time).toFormat('h:mm a'),
          duration: appointment.duration, 
        })),
        ...Array(maxAppointmentCount - slot.appointments.length).fill({
          date: slot.date,
          formattedDate: DateTime.fromISO(slot.date).toFormat('EEE d LLL'),
        }),
      ]),
    [maxAppointmentCount, slots]
  );

  const handleChange = (obj: TimePickerDateTime) => {
    if (!obj?.time) {
      return;
    }
    onChange(obj);
  };

  const noSlotsAvailable = times?.every(
    arr => Array.isArray(arr) && arr?.length === 0
  );

  return (
    <Box>
      <TimePickerNavigation
        onPrev={onPrev}
        onNext={onNext}
        showPrev={showPrev}
        showNext={showNext}
      />
      <TimePickerHeader dates={dates} />
      {noSlotsAvailable ? (
        <Text mt={5}>No available slots this week. Please try selecting a previous or next week.</Text>
      ) : (
        <TimePickerContent
          times={times}
          onChange={handleChange}
          value={value}
        />
      )}
    </Box>
  );
};
